

export const camelize = (str: string) => {
  return str.replace(/[^a-z ]/ig, '').replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) return "" // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase()
  })
}

export const toTitleCaseWithSpaces = (str: string): string => {
  if (typeof str !== 'string' || !str.length) return ''
  // Insert space before all caps and capitalize each word
  const withSpaces = str
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase to space
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // PascalCase to space
    .replace(/[_\-]+/g, ' ') // underscores/dashes to space
    .trim()
  if (!withSpaces) return ''
  // Capitalize each word
  return withSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const dollars = (num: number | bigint | string, maximumFractionDigits = 2): string => {
  let _num = num
  if (typeof _num === 'string') {
    _num = parseFloat(_num.replace(/[^0-9.]/g, ''))
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits
  }).format(_num)
}

export const dollarsToNum = (s: string | number): number => {
  if (typeof s === 'number') {
    return s
  }
  const num = parseFloat(s?.trim().replace(/[^0-9.]/g, ''))
  return isNaN(num) ? 0 : num
}

export const digit2 = (x: number | string): string => {
  let _x = x
  if (typeof _x === 'string') {
    _x = parseFloat(_x)
  }

  return _x < 10 ? '0' + _x.toString() : _x.toString()
}

export const social = (s: string): string => {
  const val = s.trim().replace(/[^0-9]/g, '')
  if (val.length < 4) return val
  if (val.length < 6) {
    return `${val.slice(0, 3)}-${val.slice(3)}`
  }

  return `${val.slice(0, 3)}-${val.slice(3, 5)}-${val.slice(5, 9)}`
}

export const getCanonicalEmail = (email: string): string => {
  
  const lowerEmail = email.toLowerCase().trim()

  const atIndex = lowerEmail.indexOf('@')

  const localPart = lowerEmail.substring(0, atIndex)
  const domain = lowerEmail.substring(atIndex)

  const plusIndex = localPart.indexOf('+')
  const normalizedLocalPart = plusIndex !== -1 ? localPart.substring(0, plusIndex) : localPart

  return normalizedLocalPart + domain
}

export const emailsAreCanonicallyEqual = (
  left?: string | null,
  right?: string | null
): boolean => {
  if (!left || !right) {
    return false
  }
  return getCanonicalEmail(left) === getCanonicalEmail(right)
}

const escapeRegexChars = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Matches emails that canonicalize to the same address, including plus-addressing
 * and case differences (e.g. user@x.com matches user+tag@x.com).
 */
export const canonicalEmailRegex = (email: string): RegExp => {
  if (!email || !email.includes('@')) {
    return /(?!)/
  }

  const canonical = getCanonicalEmail(email)
  const atIndex = canonical.lastIndexOf('@')
  if (atIndex <= 0 || atIndex === canonical.length - 1) {
    return /(?!)/
  }

  const localPart = canonical.substring(0, atIndex)
  const domain = canonical.substring(atIndex + 1)
  return new RegExp(
    `^${escapeRegexChars(localPart)}(?:\\+[^@]*)?@${escapeRegexChars(domain)}$`,
    'i'
  )
}

export const phone = (str: string): string => {
  if (!str) return ''
  const p = str.replace(/\D/g, '')
  if (p.length === 10) {
    return `(${p.substring(0, 3)}) ${p.substring(3, 6)}-${p.substring(6)}`
  }
  return p
}

export const normalizeString = (s: string | undefined | null): string => (s || '').trim().toLowerCase()

/**
 * Rounds a value in percentage points to a display string (e.g. 0.25 → "0.25%", 2.5 → "2.5%").
 * Uses one fraction digit when the hundredths place is zero, otherwise two.
 */
export const formatPercentagePoints = (percentagePoints: number): string => {
  const rounded = Math.round(percentagePoints * 100) / 100
  const hundredthsDigitIsZero = Math.round(rounded * 100) % 10 === 0
  const body = hundredthsDigitIsZero ? rounded.toFixed(1) : rounded.toFixed(2)
  return `${body}`
}
